import { useEffect } from "react";
import { Chart } from "@/components/Chart/Chart";
import { Header } from "@/components/Header/Header";
import { Toolbar } from "@/components/Toolbar/Toolbar";
import { RightSidebar } from "@/components/RightSidebar/RightSidebar";
import { DrawingToolbar } from "@/components/DrawingToolbar/DrawingToolbar";
import { ObjectsTree } from "@/components/ObjectsTree/ObjectsTree";
import { TabBar } from "@/components/TabBar/TabBar";
import { SplitDivider } from "@/components/SplitDivider/SplitDivider";
import { TradeJournal } from "@/components/TradeJournal/TradeJournal";
import { Notes } from "@/components/Notes/Notes";
import { ImageBoard } from "@/components/ImageBoard/ImageBoard";
import { HelpPanel } from "@/components/HelpPanel/HelpPanel";
import { NewsPanel } from "@/components/NewsPanel/NewsPanel";
import { ChatOnly } from "@/components/Chat/ChatOnly";
import { ChatPanel } from "@/components/Chat/ChatPanel";
import { ChartColorsDialog } from "@/components/ChartColorsDialog/ChartColorsDialog";
import { WeeklyBehaviourDialog } from "@/components/WeeklyBehaviourDialog/WeeklyBehaviourDialog";
import { Toaster } from "@/components/Toaster/Toaster";
import { ConflictBanner } from "@/components/ConflictBanner/ConflictBanner";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchFibLevels } from "@/state/slices/fibLevelsSlice";
import { fetchMe } from "@/state/slices/meSlice";
import {
  fetchUserIndicators,
  fetchUserTheme,
  fetchUserWeeklyBehaviour,
} from "@/state/slices/userPrefsSlice";
import { setFocusedPane } from "@/state/slices/tabsSlice";
import { useLiveAnalysis } from "@/hooks/useLiveAnalysis";
import { useTradingStream } from "@/hooks/useTradingStream";
import { useSignalStream } from "@/hooks/useSignalStream";
import { useNewsFeed } from "@/hooks/useNewsFeed";
import { useSignalHistoryAutoFetch } from "@/hooks/useSignalHistoryAutoFetch";
import { Tour } from "@/components/Tour/Tour";
import { useTours } from "@/components/Tour/useTours";
import { SIGNAL_TAB_ID } from "@/state/slices/signalSlice";
import { SignalPnlStrip } from "@/components/SignalChart/SignalPnlStrip";
import { SignalOrdersPanel } from "@/components/SignalChart/SignalOrdersPanel";
import { useTradingHistoryAutoFetch } from "@/hooks/useTradingHistoryAutoFetch";
import {
  useWorkspaceAutoSave,
  useWorkspaceBoot,
  useWorkspaceFlushOnHide,
} from "@/hooks/useWorkspaceAutoSave";
import { useWorkspaceLiveSync } from "@/hooks/useWorkspaceLiveSync";
import { ChatSocketContext, useChatSocket } from "@/hooks/useChatSocket";
import { Onboarding } from "@/components/Onboarding/Onboarding";
import { redirectToSignIn } from "@/utils/landing";
import { api } from "@/api/client";
import { setSymbolMetaList } from "@/utils/symbolMeta";
import { TRADE_VOLUME_STORAGE_KEY, setSymbol } from "@/state/slices/uiSlice";
import { STRATEGIES_TAB_ID } from "@/state/slices/strategiesSlice";
import { useStrategiesStream } from "@/hooks/useStrategiesStream";
import { useStrategyHistoryAutoFetch } from "@/hooks/useStrategyHistoryAutoFetch";
import { StrategyBar } from "@/components/Strategies/StrategyBar";
import { StrategyPicker } from "@/components/Strategies/StrategyPicker";
import { StrategyOrdersPanel } from "@/components/Strategies/StrategyOrdersPanel";
import { setAnalyticsEnabled, startAnalytics } from "@/analytics/queue";
import { UsageNotice } from "@/components/UsageNotice/UsageNotice";
import styles from "./App.module.css";

export default function App() {
  const dispatch = useAppDispatch();
  const tabs = useAppSelector((s) => s.tabs.tabs);
  const activeTabId = useAppSelector((s) => s.tabs.activeTabId);
  const splitTabId = useAppSelector((s) => s.tabs.splitTabId);
  const splitRatio = useAppSelector((s) => s.tabs.splitRatio);
  const focusedPaneIdx = useAppSelector((s) => s.tabs.focusedPaneIdx);
  const splitActive = splitTabId !== null;
  // Signal chart opens as a FULL-AREA view (a "Signal" tab in the TabBar), not
  // a split pane — see features/signal_chart.md. Rendered over the user's tabs
  // while open; their charts stay mounted behind so switching back is instant.
  const signalActive = useAppSelector(
    (s) => s.signal.enabled && s.ui.signalChartOpen,
  );
  // Strategies view — the same full-area pattern for the Python strategy bots
  // (features/strategy_signals.md). Separate from the Signal view; the TabBar
  // keeps the two mutually exclusive.
  const strategiesActive = useAppSelector(
    (s) => s.strategies.enabled && s.strategies.viewOpen,
  );
  const strategiesSymbol = useAppSelector((s) => s.strategies.symbol);
  const meStatus = useAppSelector((s) => s.me.status);
  // Owner and admins never see onboarding (provisioned, not self-signed-up).
  const isAdmin = useAppSelector(
    (s) => s.me.roles.includes("owner") || s.me.roles.includes("admin"),
  );
  const profileComplete = useAppSelector((s) => s.me.profileComplete);
  const analyticsOptOut = useAppSelector((s) => s.me.analyticsOptOut);
  const appTheme = useAppSelector((s) => s.appearance.theme);
  const tradeVolume = useAppSelector((s) => s.ui.tradeVolume);

  // Persist the last-used trade volume per-device so it's remembered across
  // reloads (read back as the initial value in uiSlice).
  useEffect(() => {
    try {
      localStorage.setItem(TRADE_VOLUME_STORAGE_KEY, String(tradeVolume));
    } catch {
      /* storage blocked — in-memory only */
    }
  }, [tradeVolume]);

  // Apply the light/dark UI theme to the document (the boot script in
  // index.html sets it pre-paint; this keeps it in sync). Deliberately does
  // NOT persist: this effect also runs on mount, so writing here stamped the
  // default into localStorage for visitors who never picked a theme. The
  // appearanceSlice listener persists on setTheme/toggleTheme instead.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", appTheme);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", appTheme === "light" ? "#f4f6f9" : "#06080f");
  }, [appTheme]);

  // Identity first — drives the auth gate below and the Header's
  // master-gate. Fired once on mount; email + role are stable within a
  // session, so no refetch.
  useEffect(() => {
    dispatch(fetchMe());
  }, [dispatch]);

  // User-scope data — fib levels, chart theme, Weekly Behaviour
  // defaults (the bottom of each scope cascade). Deferred until the
  // session is confirmed: firing these before /api/me resolves just
  // 401s and litters the login screen's console with errors. Runs once
  // when meStatus flips to "succeeded".
  useEffect(() => {
    if (meStatus !== "succeeded") return;
    dispatch(fetchFibLevels());
    dispatch(fetchUserTheme());
    dispatch(fetchUserWeeklyBehaviour());
    dispatch(fetchUserIndicators());
  }, [dispatch, meStatus]);

  // Per-symbol metadata (price precision, session model) — fetched once
  // so every price-formatting site can read the active symbol's decimals.
  // Failure is silent: formatting falls back to the gold default (3 dp).
  useEffect(() => {
    if (meStatus !== "succeeded") return;
    api
      .getSymbols()
      .then((r) => setSymbolMetaList(r.symbols))
      .catch(() => {
        /* keep defaults */
      });
  }, [meStatus]);

  // Presence heartbeat — keeps the management dashboard's "online" fresh
  // while the app sits idle (the SSE streams are single long-lived
  // connections, so without this an idle tab would look offline). Ping on
  // confirm, then every 30s while the tab is visible. Backend marks presence
  // for any authenticated hit, so this is a cheap no-content POST.
  useEffect(() => {
    if (meStatus !== "succeeded") return;
    void api.heartbeat();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void api.heartbeat();
    }, 30000);
    // Leaving: one last ping so "last active" is when they actually left,
    // not the last persisted beat up to ten minutes earlier. `pagehide`
    // covers closing and navigating away; `visibilitychange` covers
    // backgrounding the tab, which on mobile is how a session usually ends.
    const onLeave = () => void api.heartbeat({ leaving: true });
    const onHide = () => {
      if (document.visibilityState === "hidden") onLeave();
    };
    window.addEventListener("pagehide", onLeave);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pagehide", onLeave);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [meStatus]);

  // Usage analytics (features/usage_analytics.md): events are captured by the
  // store middleware from the first dispatch; sending starts once signed in,
  // after the user's opt-out is applied.
  useEffect(() => {
    if (meStatus !== "succeeded") return;
    setAnalyticsEnabled(!analyticsOptOut);
    startAnalytics();
  }, [meStatus, analyticsOptOut]);

  // No session (or /api/me itself errored) → bounce to the landing site,
  // where sign-in lives (a Google popup). The app deliberately has no
  // login page of its own; we never surface an auth error here.
  useEffect(() => {
    if (meStatus === "unauthenticated" || meStatus === "failed") {
      redirectToSignIn();
    }
  }, [meStatus]);

  // Single live-stream subscription at the app root. The hook reads
  // `analysis.liveTabId` and the corresponding tab's symbol, so it
  // automatically tears down + reconnects when the live owner moves
  // between tabs (or stops).
  useLiveAnalysis();

  // the trading server account / positions / orders delivered via SSE from a
  // single process-wide pump. Owner sessions open one EventSource;
  // guests are skipped (the endpoint 403s them, which would
  // otherwise loop EventSource auto-reconnect forever).
  useTradingStream();

  // Shared read-only signal source (bot_all). Open to EVERY signed-in user;
  // self-gates on the status probe so disabled envs never open the stream.
  useSignalStream();

  // Economic news snapshot — feeds BOTH the News drawer and the chart's
  // high-impact overlay, so it polls whenever either is active (and not at
  // all when neither is).
  useNewsFeed();

  // Closed-trade history backs the chart's history-order markers.
  // Fetched once on owner-confirm and again whenever an open
  // position closes (positions count drops). The journal drawer's
  // refresh button covers manual reload for everything else.
  useTradingHistoryAutoFetch();

  // Signal pane's closed-trade markers (only fetches while the pane is open).
  useSignalHistoryAutoFetch();

  // Strategy signals: live status + selected strategies' order books (SSE),
  // and their closed trades for the chart markers while the view is open.
  useStrategiesStream();
  useStrategyHistoryAutoFetch();
  // The Strategies chart is a reserved-id Chart; its symbol follows the
  // XAUUSD / BTCUSD switch in the strategy bar.
  useEffect(() => {
    dispatch(setSymbol({ tabId: STRATEGIES_TAB_ID, symbol: strategiesSymbol }));
  }, [dispatch, strategiesSymbol]);

  // Guided tours: decides which one (if any) should be on screen. Renders
  // nothing itself — <Tour /> at the bottom of the tree does that.
  useTours();

  // Workspace persistence: load the user's active workspace on boot,
  // then auto-save (debounced) on every tab/drawing/viewport change.
  // No-ops gracefully when there's no active workspace yet.
  useWorkspaceBoot();
  useWorkspaceAutoSave();
  useWorkspaceFlushOnHide();
  // Real-time hydrate: other sessions' saves to the same workspace are
  // pushed over SSE and re-fetched here, instead of surfacing as a 409
  // on our next save.
  useWorkspaceLiveSync();

  // Chat WebSocket — ONE socket for the app's lifetime (not per page), for
  // every signed-in user: the community room and each user's own support
  // ticket aren't supporter-only. Rooms are joined/left on it by the chat
  // components through `useChat()`, which reads the context provided below.
  // Not opened at all while the server's chat flag is off (me.chatEnabled).
  const chatSocket = useChatSocket();

  // Auth gate — we know who's signed in (or not).
  //   no session   → bounce to the landing site (sign-in lives there as a
  //                  Google popup); show a splash while the redirect runs.
  //   idle/loading → splash while /api/me resolves.
  // Hooks above run unconditionally (React's rules), but their network
  // calls are gated on meStatus === "succeeded", so a signed-out boot
  // fires only /api/me — no 401 barrage.
  if (meStatus !== "succeeded") {
    return (
      <div
        className={styles.app}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9aa0a6",
          fontSize: 13,
        }}
      >
        {meStatus === "idle" || meStatus === "loading"
          ? "Loading…"
          : "Redirecting…"}
      </div>
    );
  }

  // Signed in but hasn't finished sign-up → one-time onboarding form.
  // Master accounts are always profile_complete, so they skip this.
  if (!isAdmin && !profileComplete) {
    return <Onboarding />;
  }

  return (
    <ChatSocketContext.Provider value={chatSocket}>
      <div className={styles.app}>
        <ConflictBanner />
        <Header />
        <TabBar />
        <div className={styles.main}>
          <Toolbar />
          {/* One <Chart> instance per tab, stacked. The active tab's
              slot is visible; the rest are visibility: hidden so their
              charts keep size + receive their own data updates in the
              background. Tab switch = CSS class change → instant, no
              setData / setVisibleRange dance, no cross-tab state
              bleed. Each chart owns its own lightweight-charts canvas
              and the per-tab Redux slot drives it. */}
          <div className={styles.chartHost}>
            {tabs.map((tab) => {
              const isLeft = tab.id === activeTabId;
              const isRight = splitTabId !== null && tab.id === splitTabId;
              const visible = isLeft || isRight;
              // Each tab keeps a single Chart instance; CSS positions
              // it as the full chart area (single pane), the left half,
              // or the right half. All other tabs stay visibility:
              // hidden + inset: 0 in the background (they keep size so
              // their lightweight-charts canvases continue to receive
              // live ticks without re-layout cost).
              let slotClass = styles.chartSlot;
              const slotStyle: React.CSSProperties = {};
              // Which pane this slot belongs to in split mode. Used
              // for the focus highlight + mousedown-to-focus dispatch.
              let pane: 0 | 1 | null = null;
              if (isLeft && splitTabId !== null) {
                slotClass = `${styles.chartSlot} ${styles.chartSlotLeft}`;
                slotStyle.right = `${(1 - splitRatio) * 100}%`;
                pane = 0;
              } else if (isRight) {
                slotClass = `${styles.chartSlot} ${styles.chartSlotRight}`;
                slotStyle.left = `${splitRatio * 100}%`;
                pane = 1;
              } else if (isLeft) {
                slotClass = `${styles.chartSlot} ${styles.chartSlotActive}`;
              }
              // Focus indicator: subtle cyan inner border on the
              // focused pane while split is open. Single-pane mode
              // doesn't need it.
              if (splitActive && pane !== null && pane === focusedPaneIdx) {
                slotClass += ` ${styles.chartSlotFocused}`;
              }
              return (
                <div
                  key={tab.id}
                  className={slotClass}
                  style={slotStyle}
                  // mousedown-to-focus: any click inside a pane (chart
                  // canvas, drawing overlay, anything) shifts focus to
                  // that pane so Header / Toolbar / Run Analysis
                  // target it. Capture phase so it fires before the
                  // chart's own click handlers — focus is a "side
                  // effect", not consuming the click.
                  onMouseDownCapture={
                    splitActive && pane !== null && pane !== focusedPaneIdx
                      ? () => dispatch(setFocusedPane(pane!))
                      : undefined
                  }
                >
                  <Chart tabId={tab.id} isActive={visible} />
                </div>
              );
            })}
            {/* Signal chart — shared read-only bot_all reference, opened as a
                FULL-AREA view (a "Signal" tab in the TabBar), not a split pane —
                a half-width chart was too cramped. A reserved-id Chart in
                signalMode: reuses the whole chart stack (candles, live ticks,
                position/order/history overlays) but sourced from the `signal`
                slice with all trade interactions disabled. Rendered last so it
                covers the user's tabs while open; their charts stay mounted
                behind it (state preserved), and closing it reveals them again.
                Not a real tab (never in the TabBar tab list / workspace). */}
            {signalActive ? (
              <div className={`${styles.chartSlot} ${styles.chartSlotActive}`}>
                <SignalPnlStrip />
                <Chart tabId={SIGNAL_TAB_ID} isActive signalMode />
              </div>
            ) : null}
            {/* Strategies — the selected strategy bots' pending + running orders,
                each in its strategy's colour, on a read-only reference chart
                (signalMode + strategiesMode). Same full-area pattern as Signal. */}
            {strategiesActive ? (
              <div className={`${styles.chartSlot} ${styles.chartSlotActive}`}>
                <StrategyBar />
                <Chart tabId={STRATEGIES_TAB_ID} isActive signalMode strategiesMode />
              </div>
            ) : null}
            {splitTabId !== null ? <SplitDivider /> : null}
            {/* Drawing toolbar — floats above the chart when a drawing
                is selected. Reads from the focused pane's Redux slot. */}
            <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 40, pointerEvents: "auto" }}>
              <DrawingToolbar />
            </div>
          </div>
          {/* Objects tree + Trade journal + Notes are DOCKED panels between
              the chart and the right rail: each renders null when closed (no
              width) and a 420px column when open, so the chart resizes to make
              room. All can be open side by side. */}
          <ObjectsTree />
          <TradeJournal />
          <Notes />
          <SignalOrdersPanel />
          <StrategyOrdersPanel />
          <NewsPanel />
          <HelpPanel />
          <ChatOnly>
            <ChatPanel />
          </ChatOnly>
          <RightSidebar />
        </div>
        {/* Fullscreen overlay, not a docked drawer — it spans the whole
            window and sits above whichever drawer launched it. */}
        <ImageBoard />
        <ChartColorsDialog />
        <WeeklyBehaviourDialog />
        <StrategyPicker />
        <Toaster />
        <UsageNotice />
        <Tour />
      </div>
    </ChatSocketContext.Provider>
  );
}
